import {
  Actions,
  Button,
  ButtonIcon,
  Dialog,
  IconDelete,
  IconEdit,
  IconTick,
  IconVisibility,
  Spread,
  Stack,
  Text,
} from 'braid-design-system';
import React from 'react';

import { listingDisplayName } from '#shared/listingIdentity';
import type { ListingRecord } from '#shared/types';

import { DialogErrorNotice } from '../DialogErrorNotice';
import { ListingFields } from './ListingFields';
import {
  listingToFieldValues,
  toFieldUpdates,
  type ListingFieldUpdates,
  type ListingFieldValues,
} from './listingFieldUpdates';

type ListingDetailProps = {
  listing: ListingRecord;
  onApprove?: (updates: ListingFieldUpdates) => void | Promise<void>;
  onDecline?: () => void | Promise<void>;
  onUnpublish?: () => void | Promise<void>;
  onSave?: (updates: ListingFieldUpdates) => void | Promise<void>;
  showModeration?: boolean;
};

export const ListingDetail = ({
  listing,
  onApprove,
  onDecline,
  onUnpublish,
  onSave,
  showModeration,
}: ListingDetailProps) => {
  const [open, setOpen] = React.useState(false);
  const [fields, setFields] = React.useState<ListingFieldValues>(() =>
    listingToFieldValues(listing),
  );
  const [saving, setSaving] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (open) {
      setFields(listingToFieldValues(listing));
      setErrors({});
    }
  }, [listing, open]);

  const currentUpdates = () => toFieldUpdates(fields);
  const displayName = listingDisplayName(listing) ?? 'Untitled listing';
  const expiresLabel = listing.expiresAt
    ? new Date(listing.expiresAt).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

  const handleFieldChange = (
    field: keyof ListingFieldValues,
    value: string,
  ) => {
    setFields((current) => ({ ...current, [field]: value }));
  };

  const runAction = async (action: () => void | Promise<void>) => {
    setSaving(true);
    setErrors({});
    try {
      await action();
      setOpen(false);
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        setErrors((err as { errors: Record<string, string> }).errors);
      } else if (err && typeof err === 'object' && 'error' in err) {
        setErrors({ _form: String((err as { error: string }).error) });
      } else {
        setErrors({
          _form: err instanceof Error ? err.message : 'Something went wrong.',
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Spread direction="horizontal" space="small" alignY="center">
        <Stack space="xsmall">
          <Text weight="strong">{displayName}</Text>
          <Text tone="secondary">
            {listing.serviceType ? `${listing.serviceType} · ` : null}
            Submitted {new Date(listing.submittedAt).toLocaleString()}
            {expiresLabel ? ` · Expires ${expiresLabel}` : null}
          </Text>
        </Stack>
        <Actions>
          <ButtonIcon
            icon={<IconEdit />}
            variant="soft"
            onClick={() => setOpen(true)}
            label="Edit listing"
          />
          {showModeration && onApprove ? (
            <ButtonIcon
              icon={<IconTick />}
              variant="soft"
              tone="formAccent"
              onClick={() =>
                runAction(() =>
                  onApprove(toFieldUpdates(listingToFieldValues(listing))),
                )
              }
              label="Approve listing"
            />
          ) : null}
          {showModeration && onDecline ? (
            <ButtonIcon
              icon={<IconDelete />}
              variant="soft"
              tone="brandAccent"
              onClick={() => runAction(() => onDecline())}
              label="Decline listing"
            />
          ) : null}
          {onUnpublish ? (
            <ButtonIcon
              icon={<IconVisibility hidden />}
              variant="soft"
              tone="brandAccent"
              onClick={() => runAction(() => onUnpublish())}
              label="Unpublish listing"
            />
          ) : null}
        </Actions>
      </Spread>

      <Dialog
        id={`edit-listing-${listing.id}`}
        title={showModeration ? 'Review listing' : 'Edit listing'}
        open={open}
        onClose={() => {
          if (!saving) {
            setOpen(false);
          }
        }}
        width="medium"
      >
        <Stack space="medium">
          <DialogErrorNotice message={errors._form} />

          <Text tone="secondary">
            Submitted {new Date(listing.submittedAt).toLocaleString()}
            {expiresLabel ? ` · Expires ${expiresLabel}` : null}
          </Text>

          <ListingFields
            listingId={listing.id}
            fields={fields}
            errors={errors}
            onChange={handleFieldChange}
          />

          <Actions>
            {showModeration && onApprove ? (
              <Button
                tone="formAccent"
                loading={saving}
                onClick={() => runAction(() => onApprove(currentUpdates()))}
              >
                Save & approve
              </Button>
            ) : null}
            {onSave ? (
              <Button
                loading={saving}
                onClick={() => runAction(() => onSave(currentUpdates()))}
              >
                Save changes
              </Button>
            ) : null}
            {showModeration && onDecline ? (
              <Button
                tone="critical"
                variant="ghost"
                loading={saving}
                onClick={() => runAction(() => onDecline())}
              >
                Decline
              </Button>
            ) : null}
            {onUnpublish ? (
              <Button
                tone="critical"
                variant="ghost"
                loading={saving}
                onClick={() => runAction(() => onUnpublish())}
              >
                Unpublish
              </Button>
            ) : null}
            <Button
              variant="transparent"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
          </Actions>
        </Stack>
      </Dialog>
    </>
  );
};
